import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";

const TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const WARN_MS = 60 * 1000;         // warn 60 seconds before

export default function useIdleTimeout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  // Hold latest refs so the effect body doesn't need to depend on them
  // (logout is re-created every AuthContext render).
  const logoutRef = useRef(logout);
  const navRef = useRef(nav);
  useEffect(() => { logoutRef.current = logout; navRef.current = nav; }, [logout, nav]);

  const skip = !user
    || loc.pathname === "/login"
    || loc.pathname === "/forgot-password"
    || loc.pathname === "/reset-password";

  useEffect(() => {
    if (skip) return undefined;

    let lastActivity = Date.now();
    let warned = false;
    const bump = () => { lastActivity = Date.now(); warned = false; };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));

    const interval = window.setInterval(() => {
      const idle = Date.now() - lastActivity;
      if (idle >= TIMEOUT_MS) {
        toast.info("You have been signed out due to inactivity.");
        Promise.resolve(logoutRef.current()).finally(() =>
          navRef.current("/login", { replace: true })
        );
        return;
      }
      if (idle >= TIMEOUT_MS - WARN_MS && !warned) {
        warned = true;
        toast.warning("You will be signed out in 60 seconds due to inactivity. Move the mouse to stay signed in.");
      }
    }, 15000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      window.clearInterval(interval);
    };
  }, [skip]);
}
